import React from 'react';
import { usePortalStore } from '../../store';
import { Radar, TrendingUp, AlertTriangle, Zap } from 'lucide-react';

export const WatchtowerView: React.FC = () => {
  const { signals, dismissSignal } = usePortalStore();

  const signalsByCategory = signals.reduce((acc, signal) => {
    if (!acc[signal.category]) {
      acc[signal.category] = [];
    }
    acc[signal.category].push(signal);
    return acc;
  }, {} as Record<string, typeof signals>);

  const categoryConfig = {
    competitor: { label: 'Competitors', icon: '⚔️', color: 'text-red-400' },
    tech: { label: 'Tech Trends', icon: '🔧', color: 'text-blue-400' },
    market: { label: 'Market', icon: '📈', color: 'text-green-400' },
    regulatory: { label: 'Regulatory', icon: '⚖️', color: 'text-purple-400' },
    dependency: { label: 'Dependencies', icon: '🔗', color: 'text-orange-400' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Watchtower</h1>
        <p className="text-slate-400 mt-2">Environmental radar and strategic signals</p>
      </div>

      {/* Intelligence Feeds Status */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {Object.entries(categoryConfig).map(([key, config]) => {
          const count = signalsByCategory[key as keyof typeof categoryConfig]?.length || 0;
          return (
            <div key={key} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="text-2xl mb-2">{config.icon}</div>
              <h3 className="text-sm font-semibold text-slate-100 mb-1">{config.label}</h3>
              <div className={`text-lg font-bold ${config.color}`}>{count}</div>
              <div className="text-xs text-slate-400 mt-2">✓ 2h ago</div>
            </div>
          );
        })}
      </div>

      {/* Signals Radar */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Radar className="w-5 h-5" />
          Signals Radar
        </h2>

        {signals.length === 0 ? (
          <div className="text-center py-12">
            <Zap className="w-12 h-12 text-slate-600 mx-auto mb-3 opacity-50" />
            <p className="text-slate-400">No active signals detected</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Simple radar visualization */}
            <div className="flex flex-wrap gap-4 justify-center py-8">
              {signals.map(signal => (
                <div
                  key={signal.id}
                  className={`relative group cursor-pointer ${
                    signal.severity === 'critical'
                      ? 'w-20 h-20 bg-red-600 opacity-100'
                      : signal.severity === 'high'
                      ? 'w-16 h-16 bg-orange-500 opacity-90'
                      : signal.severity === 'medium'
                      ? 'w-12 h-12 bg-yellow-500 opacity-80'
                      : 'w-8 h-8 bg-blue-400 opacity-70'
                  } rounded-full animate-pulse`}
                >
                  <div className="absolute inset-0 rounded-full border-2 border-opacity-50 border-white animate-ping"></div>
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                    {signal.severity[0].toUpperCase()}
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                    {signal.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Intelligence Reports */}
      <div>
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Intelligence Reports</h2>
        <div className="space-y-3">
          {signals.length === 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 text-center">
              <p className="text-slate-400">All clear. No significant signals.</p>
            </div>
          ) : (
            signals.map(signal => (
              <div
                key={signal.id}
                className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className={`px-2 py-1 rounded text-xs font-bold text-white ${
                          signal.severity === 'critical'
                            ? 'bg-red-600'
                            : signal.severity === 'high'
                            ? 'bg-orange-600'
                            : signal.severity === 'medium'
                            ? 'bg-yellow-600'
                            : 'bg-blue-600'
                        }`}
                      >
                        {signal.severity.toUpperCase()}
                      </div>
                      <h3 className="font-semibold text-slate-100">{signal.title}</h3>
                    </div>
                    <p className="text-sm text-slate-300 mb-2">{signal.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span>Source: {signal.source}</span>
                      <span>Detected: {signal.detected.toLocaleDateString()}</span>
                      {signal.relevantTo && signal.relevantTo.length > 0 && (
                        <div className="flex gap-2">
                          <span>Relevant to:</span>
                          {signal.relevantTo.map(dept => (
                            <span
                              key={dept}
                              className="px-2 py-1 bg-slate-900 rounded"
                            >
                              {dept}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => dismissSignal(signal.id)}
                    className="text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Monitoring Configuration */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Monitoring Configuration</h2>
        <div className="space-y-2">
          {Object.entries(categoryConfig).map(([key, config]) => (
            <label key={key} className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
              <input type="checkbox" defaultChecked className="w-4 h-4 rounded" />
              <span className="text-sm text-slate-300">{config.label}</span>
              <span className="text-xs text-slate-500 ml-auto">✓ checked 2h ago</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
